import { popupTargetFromElement } from '@blocksuite/affine-components/context-menu';
import { computed, signal } from '@preact/signals-core';
import { html } from 'lit/static-html.js';

import { BaseCellRenderer } from '../../core/property/index.js';
import { createFromBaseCellRenderer } from '../../core/property/renderer.js';
import { createIcon } from '../../core/utils/uni-icon.js';
import { formatNumber } from '../number/utils/formatter.js';
import {
  formulaBadgeStyle,
  formulaErrorStyle,
  formulaPlaceholderStyle,
  formulaRowOverrideBadgeStyle,
  formulaRowOverrideWrapperStyle,
} from './cell-renderer-css.js';
import { computeFormulaValue } from './compute.js';
import { buildFormulaConfigMenu } from './config-menu.js';
import { formulaPropertyModelConfig } from './define.js';
import { openRowFormulaMenu } from './row-menu.js';
import type { FormulaPropertyData, FormulaRawValue } from './types.js';

export class FormulaCell extends BaseCellRenderer<
  FormulaRawValue,
  number,
  FormulaPropertyData
> {
  override beforeEnterEditMode(): boolean {
    return false;
  }

  private readonly _result$ = computed<number | null>(() => {
    return computeFormulaValue(
      this.view,
      this.row.rowId,
      this.property.id,
      this.property.data$.value
    );
  });

  private readonly _openRowFormulaMenu = (event?: Event) => {
    if (this.view.readonly$.value) return;

    event?.preventDefault();
    event?.stopPropagation();

    openRowFormulaMenu(
      popupTargetFromElement(this),
      this.view,
      this.property,
      this.row.rowId,
      value => this.valueSetImmediate(value)
    );
  };

  // True while this cell is the active (clicked) one; reveals the fx badge.
  private readonly _active$ = signal(false);

  override connectedCallback() {
    super.connectedCallback();
    this._disposables.addFromEvent(this, 'click', () => {
      this._active$.value = true;
    });
    this._disposables.addFromEvent(
      document,
      'pointerdown',
      (event: PointerEvent) => {
        if (!event.composedPath().includes(this)) {
          this._active$.value = false;
        }
      },
      true
    );
    this._disposables.addFromEvent(
      this,
      'contextmenu',
      this._openRowFormulaMenu
    );
  }

  override render() {
    const { expression, format, decimal, prefix, suffix } =
      this.property.data$.value;
    const effectiveExpression =
      typeof this.value === 'string' ? this.value : expression;
    if (!effectiveExpression.trim()) {
      return html`<div class="${formulaBadgeStyle} ${formulaPlaceholderStyle}">
        No formula
      </div>`;
    }
    const result = this._result$.value;
    if (result == null) {
      return html`<div class="${formulaBadgeStyle}">—</div>`;
    }
    if (Number.isNaN(result)) {
      return html`<div class="${formulaBadgeStyle} ${formulaErrorStyle}">
        Error
      </div>`;
    }
    const formatted = `${prefix ?? ''}${formatNumber(result, format, decimal)}${
      suffix ?? ''
    }`;
    // A small "fx" badge marks rows that carry a row-level formula override.
    const hasRowOverride = typeof this.value === 'string';
    return html`<div
      class="${formulaRowOverrideWrapperStyle}"
      data-active=${this._active$.value ? 'true' : 'false'}
    >
      <div class="${formulaBadgeStyle}">${formatted}</div>
      ${hasRowOverride
        ? html`<span
            class="${formulaRowOverrideBadgeStyle}"
            data-row-formula-badge
            title="Row formula"
            @click=${(event: MouseEvent) => this._openRowFormulaMenu(event)}
            >fx</span
          >`
        : null}
    </div>`;
  }
}

export const formulaPropertyConfig =
  formulaPropertyModelConfig.createPropertyMeta({
    icon: createIcon('MathPanelIcon'),
    cellRenderer: {
      view: createFromBaseCellRenderer(FormulaCell),
    },
    propertyConfigMenu: buildFormulaConfigMenu,
  });
